import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Folder, FolderOpen, ChevronLeft, ChevronRight, Star, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Cookies from 'js-cookie';
import axios from 'axios';
import ItemDetailsModal from '../../../../../components/ItemDetailsModal';
import { updateFolder, deleteFolder } from '../../../../../redux/actions/search.action';
import { configureKeyboardNavigation } from '../../../../../redux/actions/global.action';
import { BACKEND_API_URL } from '../../../../../utils/api';

const FolderResults = ({
    folders,
    totalCount,
    startIndex,
    endIndex,
    page,
    pageSize,
    onPageChange,
    headerOnly = false,
    contentOnly = false,
    title = 'Folders',
    emptyTitle = 'No folders found',
    emptyHint = 'Try adjusting your filters',
    onUpdateFolder,   // (id, updates) => void; defaults to updating search results
    onDeleteFolder,   // (id) => void; defaults to removing from search results
    rowSize = 'compact' // Key of FOLDER_ROW_SIZES
}) => {
    const dispatch = useDispatch();
    const totalPages = Math.ceil(totalCount / pageSize);

    // Handler for folder name/status/star/tags updates
    const handleUpdateFolder = (id, name, status = null, star_level = null, tags = null) => {
        const updates = {};
        if (status !== null) updates.status = status;
        else if (star_level !== null) updates.star_level = star_level;
        else if (tags !== null) updates.tags = tags;
        else updates.name = name;
        
        if (onUpdateFolder) onUpdateFolder(id, updates);
        else dispatch(updateFolder(id, updates));
    };

    // Handler for folder deletion
    const handleDeleteFolder = (id) => {
        if (onDeleteFolder) onDeleteFolder(id);
        else dispatch(deleteFolder(id));
    };
    
    // Header Component
    const Header = () => (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
                <div className="flex items-center justify-center h-6 w-6 rounded-md bg-amber-100 dark:bg-amber-900/30">
                    <Folder className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="font-semibold text-sm">{title}</span>
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-mono border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50">
                    {totalCount}
                </Badge>
            </div>
            
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground mr-1">
                        {startIndex + 1}-{endIndex}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                        onClick={(e) => onPageChange(e, page - 1)}
                        disabled={page === 1}
                    >
                        <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-[10px] font-medium min-w-[40px] text-center tabular-nums">
                        {page}/{totalPages}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                        onClick={(e) => onPageChange(e, page + 1)}
                        disabled={page === totalPages}
                    >
                        <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                </div>
            )}
            
            {totalPages <= 1 && totalCount > 0 && (
                <span className="text-[10px] text-muted-foreground font-mono">
                    {startIndex + 1}-{endIndex} of {totalCount}
                </span>
            )}
        </div>
    );

    // Content Component  
    const Content = () => (
        <>
            {folders.length > 0 ? (
                <div className="divide-y divide-border/50">
                    {folders.map((folder) => (
                        <CompactFolderRow
                            key={folder.id}
                            folder={folder}
                            size={rowSize}
                            onUpdate={handleUpdateFolder}
                            onDelete={handleDeleteFolder}
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <FolderOpen className="h-10 w-10 mb-2 opacity-20" />
                    <p className="text-xs font-medium">{emptyTitle}</p>
                    <p className="text-[10px] mt-0.5 opacity-60">{emptyHint}</p>
                </div>
            )}
        </>
    );

    if (headerOnly) {
        return <Header />;
    }

    if (contentOnly) {
        return <Content />;
    }

    // Full render (for backwards compatibility)
    return (
        <div className="w-full">
            <Header />
            <Content />
        </div>
    );
};

// Row styles per size: compact (search results) and comfortable (bookmark view)
const FOLDER_ROW_SIZES = {
    compact: {
        row: "gap-2 px-2.5 py-1.5",
        star: "h-3 w-3",
        iconBox: "h-5 w-5",
        icon: "h-3 w-3",
        name: "text-xs",
        path: "text-[9px]",
        tag: "text-[8px] max-w-[50px]",
        badge: "h-4 px-1 text-[8px]",
        avatar: "h-5 w-5 text-[8px]",
        extra: "text-[8px]",
    },
    comfortable: {
        row: "gap-3 px-3 py-2.5",
        star: "h-3.5 w-3.5",
        iconBox: "h-7 w-7",
        icon: "h-4 w-4",
        name: "text-sm",
        path: "text-[11px]",
        tag: "text-[10px] max-w-[70px]",
        badge: "h-5 px-1.5 text-[10px]",
        avatar: "h-6 w-6 text-[10px]",
        extra: "text-[10px]",
    },
};

// Compact folder row component for search results
const CompactFolderRow = ({ folder, onUpdate, onDelete, size = 'compact' }) => {
    const sizes = FOLDER_ROW_SIZES[size];
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [folderData, setFolderData] = useState(null);

    const getStatusConfig = (status) => {
        if (!status) return null;
        const customStatusOrder = ["Taking Longer", "Doing", "Doing-in-bg", "Paused", "TODO", "Block-on-asm",
            "Blocked-on-purchase", "Blocked-on-discussion", "Blocked", "Done"];
        if (status.length <= 2) {
            status = customStatusOrder[parseInt(status, 10) - 1];
        }
        const configs = {
            "Taking Longer": { label: "Slow", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
            "Doing": { label: "Active", color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
            "Doing-in-bg": { label: "BG", color: "bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300" },
            "Paused": { label: "Paused", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
            "TODO": { label: "TODO", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
            "Block-on-asm": { label: "Block", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
            "Blocked-on-purchase": { label: "Block", color: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300" },
            "Blocked-on-discussion": { label: "Block", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
            "Blocked": { label: "Block", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
            "Done": { label: "Done", color: "bg-primary/15 text-primary dark:bg-primary/25 dark:text-primary" }
        };
        return configs[status] || { label: status?.slice(0,4), color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" };
    };

    const getStarColor = (level) => {
        if (level === 3) return "text-red-500";
        if (level === 2) return "text-orange-500";
        if (level === 1) return "text-primary";
        return "text-muted-foreground/30";
    };

    const statusConfig = getStatusConfig(folder.status);
    const assignee = folder.employees?.[0];
    const extraAssignees = (folder.employees?.length || 0) - 1;

    // Fetch folder details when modal opens
    const fetchFolderDetails = useCallback(async () => {
        try {
            const accessToken = Cookies.get("access_token");
            const response = await axios.get(`${BACKEND_API_URL}/api/folder/${folder.id}`, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            setFolderData(response.data);
        } catch (error) {
            console.error('Error fetching folder details:', error);
        }
    }, [folder.id]);

    // Open modal handler
    const handleOpen = useCallback(() => {
        setIsOpen(true);
        dispatch(configureKeyboardNavigation(false));
        fetchFolderDetails();
    }, [dispatch, fetchFolderDetails]);

    // Close modal handler
    const handleClose = useCallback(() => {
        setIsOpen(false);
        dispatch(configureKeyboardNavigation(true));
    }, [dispatch]);

    // Handle click events (single-click navigates, double-click/right-click opens modal)
    const handleClick = (e) => {
        e.preventDefault();
        
        if (e.type === 'click' && e.detail === 2) {
            // Double-click: open modal
            handleOpen();
        } else if (e.type === 'contextmenu') {
            // Right-click: open modal
            e.preventDefault();
            handleOpen();
        } else if (e.type === 'click' && e.detail === 1) {
            // Single-click: open folder in new tab
            window.open(`/folder/${folder.id}`, '_blank');
        }
    };

    // Handler for folder updates from modal
    const handleUpdateFolder = (id, name, status = null, starLevel = null, isArchived = null, tags = null) => {
        const updates = {};
        if (status !== null) updates.status = status;
        else if (starLevel !== null) updates.star_level = starLevel;
        else if (isArchived !== null) updates.is_archived = isArchived;
        else if (tags !== null) updates.tags = tags;
        else updates.name = name;
        
        onUpdate(id, name, status, starLevel, tags);
    };

    return (
        <>
            <div 
                onClick={handleClick}
                onContextMenu={handleClick}
                data-result-row
                className={cn(
                    "group flex items-center cursor-pointer",
                    sizes.row,
                    "hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors duration-150",
                    folder.is_archived && "opacity-60"
                )}
            >
                {/* Star indicator */}
                <div className="shrink-0 w-4">
                    {folder.star_level > 0 && (
                        <Star className={cn("fill-current", sizes.star, getStarColor(folder.star_level))} />
                    )}
                </div>

                {/* Folder icon with status color */}
                <div className={cn(
                    "shrink-0 rounded flex items-center justify-center",
                    sizes.iconBox,
                    statusConfig ? statusConfig.color : "bg-amber-100/50 dark:bg-amber-900/20"
                )}>
                    <Folder className={sizes.icon} />
                </div>

                {/* Name and path */}
                <div className="flex-1 min-w-0 flex flex-col">
                    <span className={cn(
                        "font-medium truncate",
                        sizes.name,
                        folder.is_archived ? "text-muted-foreground" : "text-foreground"
                    )}>
                        {folder.name}
                    </span>
                    {folder.path_string && (
                        <span className={cn("text-muted-foreground/70 truncate leading-tight", sizes.path)} title={folder.path_string}>
                            {folder.path_string}
                        </span>
                    )}
                </div>

                {/* Tags - show max 2 */}
                {folder.tags?.length > 0 && (
                    <div className="hidden sm:flex items-center gap-0.5 shrink-0">
                        {folder.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className={cn("inline-flex items-center px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 truncate", sizes.tag)}>
                                {tag.name}
                            </span>
                        ))}
                        {folder.tags.length > 2 && (
                            <span className={cn("text-muted-foreground", sizes.extra)}>+{folder.tags.length - 2}</span>
                        )}
                    </div>
                )}

                {/* Status badge */}
                {statusConfig && (
                    <Badge variant="outline" className={cn(
                        "font-medium border-0 shrink-0",
                        sizes.badge,
                        statusConfig.color
                    )}>
                        {statusConfig.label}
                    </Badge>
                )}

                {/* Assignee */}
                {assignee && (
                    <div className="shrink-0 flex items-center gap-1" title={`${assignee.first_name} ${assignee.last_name}`}>
                        <div 
                            className={cn("rounded-md flex items-center justify-center font-semibold text-white", sizes.avatar)}
                            style={{ backgroundColor: `hsl(${Math.abs([...`${assignee.first_name}${assignee.last_name}`].reduce((h, c) => c.charCodeAt(0) + ((h << 5) - h), 0) % 360)}, 55%, 50%)` }}
                        >
                            {assignee.first_name.slice(0,1)}{assignee.last_name.slice(0,1)}
                        </div>
                        {extraAssignees > 0 && (
                            <span className={cn("text-muted-foreground", sizes.extra)}>+{extraAssignees}</span>
                        )}
                    </div>
                )}

                {/* Open link indicator */}
                <ExternalLink className="h-3 w-3 text-muted-foreground/0 group-hover:text-muted-foreground/50 transition-colors shrink-0" />
            </div>

            {/* Item Details Modal */}
            {folderData && (
                <ItemDetailsModal
                    isOpen={isOpen}
                    handleClose={handleClose}
                    depth={-1}
                    itemData={folder}
                    itemInfo={folderData}
                    updateNameHandler={handleUpdateFolder}
                    setItemData={setFolderData}
                    on_delete={onDelete}
                    itemType="folder"
                    fetchItemDetails={fetchFolderDetails}
                />
            )}
        </>
    );
};

export default FolderResults;
